import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock } from '@fortawesome/free-solid-svg-icons'
import GrowthWheelPagination from '../../components/GrowthWheelPagination/GrowthWheelPagination'
import { getPageTransitionClass } from '../../utils/growth-wheel-utils'
import { useGrowthWheelPagination } from '../../hooks/useGrowthWheelPagination'
import SubCompetencyAssessmentContent from '../../components/GrowthWheelComponents/SubCompetencyAssessmentContent'
import { useContentTransition } from '../../hooks/useContentTransition'
import BackButton from '../../components/GrowthWheelComponents/BackButton'
import { setCurrentSubCompetency } from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'
import { SubCompetencyWithEvaluation } from '../../types/GrowthWheel'
import SubCompetencyAssessmentTitleCard from '../../components/GrowthWheelComponents/SubCompetencyAssessmentTitleCard'

interface Props {
  isAdminView?: boolean
  userName?: string
  guildName?: string
  subjectUserId: number
  reviewerUserId?: number
}

const GrowthWheelSubCompetencyAssessment = ({
  isAdminView = false,
  userName,
  guildName,
  subjectUserId,
  reviewerUserId,
}: Props) => {
  const dispatch = useDispatch()
  const assessmentCompleted = useSelector(
    (state: RootState) => state.growthWheel.assessmentCompleted,
  )
  const assessmentCompletedAt = useSelector(
    (state: RootState) => state.growthWheel.assessmentCompletedAt,
  )
  const isReadOnly = useSelector(
    (state: RootState) => state.growthWheel.isReadOnly,
  )
  const currentCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentCompetency,
  )
  const currentSubCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentSubCompetency,
  )

  const subCompetencyList = currentCompetency?.subCompetencies ?? []
  const completedDate = assessmentCompletedAt
    ? new Date(assessmentCompletedAt).toLocaleDateString()
    : null
  const isLocked = assessmentCompleted || isReadOnly
  const lockedBannerText = assessmentCompleted
    ? `Viewing completed assessment${
        completedDate ? ` completed on ${completedDate}` : ''
      }. All fields are locked.`
    : 'Viewing a historical assessment session. Scores and notes are read-only.'

  const handleClickSubCompetencyChange = (
    sub: SubCompetencyWithEvaluation | null,
  ) => {
    dispatch(setCurrentSubCompetency(sub))
  }

  if (!currentSubCompetency) return null
  const { currentIndex, handlePrevious, handleNext } = useGrowthWheelPagination(
    subCompetencyList,
    currentSubCompetency,
    handleClickSubCompetencyChange,
  )
  const { isTransitioning, transitionDirection } =
    useContentTransition(currentIndex)

  return (
    <div className="container mb-5 gw-competency-area-view">
      {userName ? (
        <div className="gw-competency-area-view-header gw-competency-area-view-header--competency-assessment">
          <BackButton
            onClick={() => handleClickSubCompetencyChange(null)}
            text="Back to All Sub-competencies"
          />
          <div className="gw-growth-wheel-header-titles">
            <h1 className="gw-growth-wheel-title">
              {`${userName}'s`} Growth Wheel
            </h1>
            {guildName ? (
              <p className="gw-growth-wheel-guild-name">{guildName}</p>
            ) : null}
          </div>
        </div>
      ) : (
        <BackButton
          onClick={() => handleClickSubCompetencyChange(null)}
          text="Back to All Sub-competencies"
        />
      )}

      {isLocked && (
        <div className="gw-assessment-locked-banner">
          <FontAwesomeIcon icon={faLock} />
          <span>{lockedBannerText}</span>
        </div>
      )}

      <div
        className={`gw-competency-area-view-content gw-competency-area-page ${getPageTransitionClass(
          isTransitioning,
          transitionDirection,
        )}`}
      >
        <SubCompetencyAssessmentTitleCard
          name={currentSubCompetency.name}
          description={currentSubCompetency.description}
          isFocus={currentSubCompetency.evaluation?.focus}
          evaluationId={currentSubCompetency.evaluation?.id}
          subjectUserId={subjectUserId}
          subCompetencyId={currentSubCompetency.id}
          isReadOnly={isLocked}
        />
      </div>

      <div
        className={`gw-competency-assessment-content ${getPageTransitionClass(
          isTransitioning,
          transitionDirection,
        )}`}
      >
        <SubCompetencyAssessmentContent
          competency={currentSubCompetency}
          canEditSelf={!isAdminView && !isLocked}
          canEditGuided={isAdminView && !isLocked}
          isLocked={isLocked}
          userRole={isAdminView ? 'admin' : 'user'}
          subjectUserId={subjectUserId}
          reviewerUserId={reviewerUserId}
        />
      </div>

      <GrowthWheelPagination
        currentIndex={currentIndex}
        optionsList={subCompetencyList}
        handleClickPrevious={handlePrevious}
        handleClickNext={handleNext}
        type="Sub-competency"
      />
    </div>
  )
}

export default GrowthWheelSubCompetencyAssessment
